// ============================================================================
// OBT — Nihai karar satırı BAĞLAMI (madde 7)
// ============================================================================
// Müdür "Dahil Et / Çıkar / Ertele" kararını verirken parkın CEVABINI görmeli:
// soru→cevap, genel not, fotoğraflar ve (varsa) revizyon geçmişi. Salt-okunur;
// veri `GET /responses/{id}/review` (ReviewDetailOut) ucundan gelir (Müdür bu
// cevabın kademe zincirinde olduğundan yetkilidir).
// ============================================================================

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { getReviewDetail } from './reviewApi';
import {
  REVISION_EVENT_LABELS,
  REVIEW_ACTION_LABELS,
  labelFor,
} from './reviewEnums';
import PhotoThumb from '../response/PhotoThumb';

function answerText(a) {
  if (a?.answer_boolean === true) return 'Evet';
  if (a?.answer_boolean === false) return 'Hayır';
  if (Array.isArray(a?.option_values) && a.option_values.length > 0) {
    return a.option_values.join(', ');
  }
  if (a?.answer_text) return a.answer_text;
  return '—';
}

export default function DecisionRowDetail({ responseId }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, error: null, detail: null });

  useEffect(() => {
    if (!open || !responseId || state.detail) return undefined;
    const controller = new AbortController();
    setState({ loading: true, error: null, detail: null });
    getReviewDetail(responseId, { signal: controller.signal })
      .then((detail) => setState({ loading: false, error: null, detail }))
      .catch((e) => {
        if (controller.signal.aborted) return;
        setState({ loading: false, error: e, detail: null });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, responseId]);

  const d = state.detail;

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-1 text-xs text-blue-700 hover:text-blue-900"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ChevronRight className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        )}
        {open ? 'Cevabı gizle' : 'Cevabı / geçmişi gör'}
      </Button>

      {open && (
        <div className="mt-1.5 rounded-md bg-gray-50 p-2.5 text-sm">
          {state.loading && (
            <p className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Cevap yükleniyor…
            </p>
          )}
          {state.error && (
            <p className="text-xs text-red-600">Cevap yüklenemedi.</p>
          )}
          {d && (
            <div className="space-y-2">
              {/* Soru → cevap */}
              <ul className="space-y-1.5">
                {(d.answers || []).map((a) => (
                  <li key={String(a.question_id)}>
                    <p className="text-gray-700">
                      <span className="text-gray-500">
                        {a.question_text || 'Soru'}:
                      </span>{' '}
                      <span className="font-medium text-gray-900">
                        {answerText(a)}
                      </span>
                    </p>
                    {Array.isArray(a.photo_ids) && a.photo_ids.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {a.photo_ids.map((pid) => (
                          <PhotoThumb
                            key={pid}
                            responseId={responseId}
                            photoId={pid}
                          />
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {/* Genel not */}
              {d.response_text && (
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Formen notu:</span> {d.response_text}
                </p>
              )}

              {/* Soruya bağlı olmayan fotoğraflar */}
              {Array.isArray(d.photo_ids) && d.photo_ids.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {d.photo_ids.map((pid) => (
                    <PhotoThumb key={pid} responseId={responseId} photoId={pid} />
                  ))}
                </div>
              )}

              {/* Revizyon konuşması (kim ne istedi / Formen yanıtı) */}
              {Array.isArray(d.review_actions) &&
                d.review_actions.filter((a) =>
                  ['bm_revision_requested', 'my_revision_requested', 'bm_rejected'].includes(
                    a.action
                  )
                ).length > 0 && (
                  <div className="rounded bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200">
                    <p className="mb-0.5 font-semibold">Revizyon geçmişi</p>
                    <ul className="space-y-0.5">
                      {d.review_actions
                        .filter((a) =>
                          [
                            'bm_revision_requested',
                            'my_revision_requested',
                            'bm_rejected',
                          ].includes(a.action)
                        )
                        .map((a) => (
                          <li key={String(a.review_action_id)}>
                            <span className="font-medium uppercase">
                              {a.review_level}
                            </span>
                            {a.actor_name ? ` · ${a.actor_name}` : ''}{' '}
                            {labelFor(REVIEW_ACTION_LABELS, a.action)}
                            {a.comment ? `: “${a.comment}”` : ''}
                          </li>
                        ))}
                      {(d.revisions || [])
                        .filter((r) => r.event === 'resubmitted' && r.comment)
                        .map((r) => (
                          <li key={r.revision_number} className="text-gray-700">
                            {labelFor(REVISION_EVENT_LABELS, r.event)}
                            {r.actor_name ? ` · ${r.actor_name}` : ''}: “{r.comment}”
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
